import React, { useMemo, useState, useEffect, useRef } from 'react';
import { AlertTriangle, Info, Plus, Car, Crown, Bike, Shield } from 'lucide-react';
import { WarrantySelectionCard } from '@/features/contracts/components/form-fields/WarrantySelectionCard';
import { useWarranties } from '@/features/warranties/hooks/useWarranties';
import { useWarrantyDetails } from '@/features/warranties/hooks/useWarrantyDetails';
import { useAllowedDurations } from '@/features/warranties/hooks/useAllowedDurations';
import { useEffectiveSocietyId } from '@/features/warranties/hooks/useEffectiveSocietyId';
import { useIsWarrantyPolicyOwner } from '@/features/warranties/hooks/useIsWarrantyPolicyOwner';
import { useSociety } from '@/features/societies/hooks/useSocieties';
import { useEffectiveConfiguration } from '@/features/societies/hooks/useSocietyConfiguration';
import { useAllowedVehicleTypes } from '@/features/warranties/hooks/useAllowedVehicleTypes';
import { useSocietyVendorType } from '@/features/societies/hooks/useSocietyVendorType';
import { usePdfPreviewStore } from '@/features/contracts/stores/pdfPreviewStore';
import { useAssistanceVisibility } from '@/features/assistance/hooks/useAssistanceVisibility';
import { ASSISTANCE_LABEL } from '@/features/assistance/assistanceWording';

import { FormInput } from '@/Components/shared/FormInput';
import { Checkbox } from '@/Components/ui/Checkbox';
import { Tooltip } from '@/Components/ui/Tooltip';
import { calculateMonthlyPrice } from '@/features/warranties/services/pricingCalculator';
import type { ContractFormState } from '@/features/contracts/hooks/useContractFormWizard';
import { usePricingInfo } from '@/features/contracts/hooks/usePricingInfo';
import { isPlausibleContractDate } from '@/features/contracts/services/contractValidator';
import { useVendorWording } from '@/hooks/useVendorWording';
import { usePermissions } from '@/hooks/auth/usePermissions';
import { CreateCustomWarrantyModal } from '@/features/warranties/components/CreateCustomWarrantyModal';
import { WARRANTY_TYPES, WARRANTY_TYPE_LABELS, type Warranty, type WarrantyListItem } from '@/features/warranties/types/warranty.types';

interface Step3Props {
  formData: ContractFormState;
  updateFormData: (updates: Partial<ContractFormState>) => void;
  onCanProceedChange: (canProceed: boolean) => void;
  showErrors?: boolean;
}

const VEHICLE_TYPE_OPTIONS = [
  { value: '', label: 'Tous les véhicules' },
  { value: '0', label: 'Standard' },
  { value: '1', label: 'Collection' },
  { value: '2', label: 'Deux-roues' },
];

function calculateMonthsAndDays(date1: Date, date2: Date): { months: number; days: number } {
  const earlier = date1 < date2 ? date1 : date2;
  const later = date1 < date2 ? date2 : date1;
  let months = 0;
  let days = 0;
  const tempDate = new Date(earlier);

  while (tempDate.getMonth() !== later.getMonth() || tempDate.getFullYear() !== later.getFullYear()) {
    tempDate.setMonth(tempDate.getMonth() + 1);
    if (tempDate <= later) {
      months++;
    } else {
      tempDate.setMonth(tempDate.getMonth() - 1);
      break;
    }
  }

  const diffTime = later.getTime() - tempDate.getTime();
  days = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  return { months, days };
}

function formatDateDifference(months: number, days: number, isPast: boolean): string {
  const parts: string[] = [];
  if (months > 0) parts.push(`${months} mois`);
  if (days > 0) parts.push(`${days} jour${days > 1 ? 's' : ''}`);
  const duration = parts.join(' et ');
  return isPast ? `Prise d'effet rétroactive de ${duration}` : `Prise d'effet différée de ${duration}`;
}

/**
 * Step 3 : Garantie + Configuration + Tarification + Signature
 */
export function Step3WarrantyConfiguration({ formData, updateFormData, onCanProceedChange, showErrors = false }: Step3Props) {
  const { can } = usePermissions();
  const { isOpen: isPdfPanelOpen } = usePdfPreviewStore();
  const [selectedDuration, setSelectedDuration] = useState<string>('');
  const [selectedVehicleType, setSelectedVehicleType] = useState<string>('');
  const [selectedWarrantyType, setSelectedWarrantyType] = useState<string>('');
  // '1' = avec assistance, '0' = sans, '' = toutes. Local au composant, comme ses
  // trois voisins : ce filtre n'entre PAS dans le snapshot sessionStorage du
  // wizard, sinon un « Sans » vieux de trois heures masquerait au retour la
  // garantie restaurée du brouillon.
  const [selectedAssistance, setSelectedAssistance] = useState<string>('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Garantie personnalisée — stockée dans formData pour survivre au changement d'étape
  const customWarranty = formData.customWarranty ?? null;
  const customWarrantyPartIds = formData.customWarrantyPartIds ?? [];
  const customWarrantyRaw = formData.customWarrantyRaw ?? null;

  const { data: society } = useSociety(formData.societyId);
  const wording = useVendorWording(formData.societyId);
  const { data: config } = useEffectiveConfiguration(formData.societyId);
  // Société du CONTRAT et non celle qui porte les garanties : l'assistance est une
  // capacité du compte vendeur. Aucune requête de plus, `useEffectiveConfiguration`
  // ci-dessus a déjà chargé la même clé React Query.
  const { isVisible: isAssistanceVisible } = useAssistanceVisibility(formData.societyId);
  const isWarrantyPolicyOwner = useIsWarrantyPolicyOwner(formData.societyId);
  const canEditPrice = can('warranty.edit_price') && !config?.lockChildrenPriceEditing;
  const societyHasStripe = !!config?.stripeAccountId;
  const { allowedVehicleTypes, isSingleType } = useAllowedVehicleTypes(formData.societyId);
  const { autoWarrantyType } = useSocietyVendorType(formData.societyId);
  const { data: warranty } = useWarrantyDetails(formData.warrantyUuid);
  const { pricingSummary, priceInfo, priceFieldConfig } = usePricingInfo(formData);

  // Initialiser les refs avec les valeurs courantes pour éviter de ré-initialiser au remount
  const hasInitializedPrice = useRef(!!formData.warrantyUuid);
  const hasInitializedTransferPrices = useRef(!!formData.warrantyUuid);
  const previousWarrantyUuid = useRef<string | null>(formData.warrantyUuid || null);

  // Réinitialiser les flags uniquement quand la garantie CHANGE (pas au remount avec la même)
  useEffect(() => {
    if (warranty?.uuid && warranty.uuid !== previousWarrantyUuid.current && previousWarrantyUuid.current !== null) {
      hasInitializedPrice.current = false;
      hasInitializedTransferPrices.current = false;
    }
    if (warranty?.uuid) {
      previousWarrantyUuid.current = warranty.uuid;
    }
  }, [warranty?.uuid]);

  // Auto-sélection du filtre véhicule si un seul type autorisé
  useEffect(() => {
    if (isSingleType && allowedVehicleTypes.length === 1) {
      setSelectedVehicleType(allowedVehicleTypes[0].toString());
    }
  }, [isSingleType, allowedVehicleTypes]);

  const effectiveSocietyId = useEffectiveSocietyId(formData.societyId);
  const allowedDurations = useAllowedDurations(formData.societyId);

  const { data: warrantiesData, isLoading } = useWarranties({
    societyId: effectiveSocietyId,
    isActive: true,
    isBroadcast: true,
    sortBy: 'order',
    sortDirection: 'ASC',
    limit: 500,
  });

  // Valeurs réellement présentes dans les garanties chargées
  const { availableDurations, availableVehicleTypes, availableWarrantyTypes, hasWithAssistance, hasWithoutAssistance } = useMemo(() => {
    const warranties = warrantiesData?.warranties || [];
    return {
      availableDurations: new Set(warranties.map((w) => w.duration)),
      availableVehicleTypes: new Set(warranties.map((w) => w.vehicleType)),
      availableWarrantyTypes: new Set(warranties.map((w) => w.warrantyType)),
      // Facettes dérivées du jeu chargé, comme les trois autres filtres : les
      // garanties arrivent en un seul appel (`limit: 500`), donc rien à demander
      // au serveur — et un filtre serveur exclurait la garantie personnalisée,
      // qui n'existe que côté client.
      hasWithAssistance: warranties.some((w) => w.assistanceFormulaId != null),
      hasWithoutAssistance: warranties.some((w) => w.assistanceFormulaId == null),
    };
  }, [warrantiesData]);

  // Filtrer les options de type de véhicule (config société + données réelles)
  const filteredVehicleTypeOptions = useMemo(() => {
    const filtered = VEHICLE_TYPE_OPTIONS.filter(
      (opt) => opt.value === '' || (allowedVehicleTypes.includes(parseInt(opt.value)) && availableVehicleTypes.has(parseInt(opt.value))),
    );
    if (isSingleType || filtered.filter((opt) => opt.value !== '').length <= 1) {
      return filtered.filter((opt) => opt.value !== '');
    }
    return filtered;
  }, [allowedVehicleTypes, isSingleType, availableVehicleTypes]);

  const durationOptions = useMemo(() => {
    const options = [{ value: '', label: 'Toutes les durées' }];
    allowedDurations.forEach((duration) => {
      if (availableDurations.has(duration)) {
        options.push({ value: duration.toString(), label: `${duration} mois` });
      }
    });
    // Si une seule durée disponible, pas besoin du filtre "Toutes"
    if (options.length === 2) {
      return options.filter((opt) => opt.value !== '');
    }
    return options;
  }, [allowedDurations, availableDurations]);

  const warrantyTypeOptions = useMemo(() => {
    const options: { value: string; label: string }[] = [{ value: '', label: 'Tous les types' }];
    Object.entries(WARRANTY_TYPE_LABELS).forEach(([key, label]) => {
      const typeValue = parseInt(key, 10);
      if (availableWarrantyTypes.has(typeValue)) {
        options.push({ value: key, label });
      }
    });
    if (options.length === 2) {
      return options.filter((opt) => opt.value !== '');
    }
    return options;
  }, [availableWarrantyTypes]);

  const assistanceOptions = useMemo(() => {
    const options: { value: string; label: string }[] = [{ value: '', label: 'Toutes' }];
    if (hasWithAssistance) options.push({ value: '1', label: `Avec ${ASSISTANCE_LABEL}` });
    if (hasWithoutAssistance) options.push({ value: '0', label: `Sans ${ASSISTANCE_LABEL}` });
    // Une seule valeur utile : le filtre ne trierait rien, on retire « Toutes »
    // et le groupe entier disparaît (même règle que durée et type de contrat).
    if (options.length === 2) {
      return options.filter((opt) => opt.value !== '');
    }
    return options;
  }, [hasWithAssistance, hasWithoutAssistance]);

  // Réinitialiser les filtres si la valeur sélectionnée n'existe plus dans les données
  useEffect(() => {
    if (selectedDuration && !availableDurations.has(parseInt(selectedDuration, 10))) {
      setSelectedDuration('');
    }
  }, [availableDurations, selectedDuration]);

  useEffect(() => {
    if (selectedVehicleType && !availableVehicleTypes.has(parseInt(selectedVehicleType, 10))) {
      setSelectedVehicleType('');
    }
  }, [availableVehicleTypes, selectedVehicleType]);

  useEffect(() => {
    if (selectedWarrantyType && !availableWarrantyTypes.has(parseInt(selectedWarrantyType, 10))) {
      setSelectedWarrantyType('');
    }
  }, [availableWarrantyTypes, selectedWarrantyType]);

  // Même garde, plus le cas propre à ce filtre : la visibilité peut retomber à
  // faux (un INTERNAL qui change de société en cours d'assistant), ce qui
  // laisserait un filtre actif et invisible vidant la grille. Pas besoin
  // d'attendre `isKnown` : revenir à '' élargit la liste, ça ne masque jamais
  // rien, et l'état n'est pas persisté.
  useEffect(() => {
    if (!selectedAssistance) return;
    const stillAvailable = selectedAssistance === '1' ? hasWithAssistance : hasWithoutAssistance;
    if (!stillAvailable || !isAssistanceVisible) {
      setSelectedAssistance('');
    }
  }, [hasWithAssistance, hasWithoutAssistance, isAssistanceVisible, selectedAssistance]);

  const isExtensionSelected = selectedWarrantyType === WARRANTY_TYPES.EXTENSION.toString();

  const filteredWarranties = useMemo(() => {
    const warranties = warrantiesData?.warranties || [];
    let filtered = warranties;

    // Masquer les garanties monthly_only si la société n'a pas Stripe
    if (!societyHasStripe) {
      filtered = filtered.filter((w) => !(w.isMensualisable && !w.isCashPayment));
    }

    if (selectedWarrantyType) {
      const warrantyType = parseInt(selectedWarrantyType, 10);
      filtered = filtered.filter((w) => w.warrantyType === warrantyType);
    }

    if (selectedVehicleType) {
      const vehicleType = parseInt(selectedVehicleType, 10);
      filtered = filtered.filter((w) => w.vehicleType === vehicleType);
    }

    if (selectedAssistance) {
      const wantsAssistance = selectedAssistance === '1';
      filtered = filtered.filter((w) => (w.assistanceFormulaId != null) === wantsAssistance);
    }

    if (selectedDuration) {
      const duration = parseInt(selectedDuration, 10);
      filtered = filtered.filter((w) => w.duration === duration);
    } else {
      filtered = filtered.filter((w) => w.warrantyType === WARRANTY_TYPES.EXTENSION || allowedDurations.includes(w.duration));
    }

    // Prépendre la garantie personnalisée si elle passe les filtres
    // (le tri par `order` ASC est déjà appliqué côté API)
    if (customWarranty) {
      const matchesVehicleType = !selectedVehicleType || customWarranty.vehicleType === parseInt(selectedVehicleType, 10);
      const matchesWarrantyType = !selectedWarrantyType || customWarranty.warrantyType === parseInt(selectedWarrantyType, 10);
      // Une garantie personnalisée n'a jamais d'assistance : sans ce test elle
      // resterait affichée sous le filtre « Avec ».
      const matchesAssistance = !selectedAssistance
        || (customWarranty.assistanceFormulaId != null) === (selectedAssistance === '1');
      const matchesDuration = customWarranty.warrantyType === WARRANTY_TYPES.EXTENSION || (selectedDuration
        ? customWarranty.duration === parseInt(selectedDuration, 10)
        : allowedDurations.includes(customWarranty.duration as typeof allowedDurations[number]));

      if (matchesVehicleType && matchesDuration && matchesWarrantyType && matchesAssistance) {
        return [customWarranty, ...filtered];
      }
    }

    return filtered;
  }, [warrantiesData, selectedWarrantyType, selectedVehicleType, selectedDuration, selectedAssistance, allowedDurations, customWarranty]);

  const dateValidation = useMemo(() => {
    if (!formData.subscriptionDate) return null;
    const selectedDate = new Date(formData.subscriptionDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const oneMonthAgo = new Date(today);
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    const oneYearAhead = new Date(today);
    oneYearAhead.setFullYear(oneYearAhead.getFullYear() + 1);

    if (selectedDate < oneMonthAgo) {
      return { type: 'warning' as const, message: 'Attention : La date sélectionnée est \u00e0 plus de 1 mois dans le passé.' };
    }
    if (selectedDate > oneYearAhead) {
      return { type: 'warning' as const, message: 'Attention : La date sélectionnée est \u00e0 plus de 1 an dans le futur.' };
    }
    return null;
  }, [formData.subscriptionDate]);

  const dateInfo = useMemo(() => {
    if (!formData.subscriptionDate) return null;
    const selectedDate = new Date(formData.subscriptionDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);

    if (selectedDate.getTime() === today.getTime()) {
      return { type: 'info' as const, message: 'Prise d\'effet immédiate' };
    }
    if (selectedDate < today) {
      const { months, days } = calculateMonthsAndDays(selectedDate, today);
      return { type: 'info' as const, message: formatDateDifference(months, days, true) };
    }
    if (selectedDate > today) {
      const { months, days } = calculateMonthsAndDays(today, selectedDate);
      return { type: 'info' as const, message: formatDateDifference(months, days, false) };
    }
    return null;
  }, [formData.subscriptionDate]);

  const isSelectedWarrantyExtension = warranty?.warrantyType === WARRANTY_TYPES.EXTENSION;

  // Info durée calculée pour les extensions de garantie constructeur
  const extensionDurationInfo = useMemo(() => {
    if (!isSelectedWarrantyExtension || !formData.subscriptionDate || !formData.extensionEndDate) return null;
    const startDate = new Date(formData.subscriptionDate);
    const endDate = new Date(formData.extensionEndDate);
    if (endDate <= startDate) return { type: 'error' as const, message: 'La date de fin doit être postérieure à la date de début', months: 0 };

    let months = (endDate.getFullYear() - startDate.getFullYear()) * 12
      + (endDate.getMonth() - startDate.getMonth());
    const remainingDays = endDate.getDate() - startDate.getDate();
    if (remainingDays > 0) months++;
    months = Math.max(1, months);

    const exactMonths = (endDate.getFullYear() - startDate.getFullYear()) * 12
      + (endDate.getMonth() - startDate.getMonth());
    const days = endDate.getDate() - startDate.getDate();
    const daysLabel = days > 0 ? ` et ${days} jour${days > 1 ? 's' : ''}` : '';

    return {
      type: 'info' as const,
      message: `Durée : ${exactMonths} mois${daysLabel}`,
      months,
    };
  }, [isSelectedWarrantyExtension, formData.subscriptionDate, formData.extensionEndDate]);

  // Réinitialiser extensionEndDate si la garantie sélectionnée n'est plus une extension
  useEffect(() => {
    if (!isSelectedWarrantyExtension && formData.extensionEndDate) {
      updateFormData({ extensionEndDate: undefined });
    }
  }, [isSelectedWarrantyExtension, formData.extensionEndDate, updateFormData]);

  useEffect(() => {
    if (warranty && config && !hasInitializedPrice.current) {
      {
        const isMonthlyOnly = !warranty.isCashPayment && warranty.isMensualisable;
        const isBoth = warranty.isCashPayment && warranty.isMensualisable;

        if (isMonthlyOnly) {
          updateFormData({
            monthlyPrice: warranty.sellingPrice,
            sellingPrice: warranty.sellingPrice * warranty.duration
          });
        } else if (isBoth) {
          const monthlyPriceWithFees = calculateMonthlyPrice(
            warranty.sellingPrice / warranty.duration,
            config.monthlyPlateformFees
          );
          updateFormData({
            sellingPrice: warranty.sellingPrice,
            monthlyPriceWithFees: Number(monthlyPriceWithFees.toFixed(2))
          });
        } else {
          updateFormData({ sellingPrice: warranty.sellingPrice });
        }
      }

      hasInitializedPrice.current = true;
    }
  }, [warranty, config]);

  useEffect(() => {
    if (warranty && !hasInitializedTransferPrices.current) {
      updateFormData({
        headTransferPrice: Number(warranty.headTransferPrice) || 0,
        memberTransferPrice: Number(warranty.memberTransferPrice) || 0,
      });
      hasInitializedTransferPrices.current = true;
    }
  }, [warranty]);

  // Détermine si l'inclusion est possible selon la configuration de la société
  const canBeIncluded = useMemo(() => {
    if (!config) return false;
    return !!config.warrantyIncludedOption;
  }, [config]);

  useEffect(() => {
    if (pricingSummary?.monthlyPriceWithFees) {
      updateFormData({ monthlyPriceWithFees: pricingSummary.monthlyPriceWithFees });
    }
  }, [pricingSummary?.monthlyPriceWithFees]);

  const handleWarrantyCreated = (created: Warranty, partIds?: number[]) => {
    setIsCreateModalOpen(false);
    setIsEditMode(false);

    // Mapper Warranty → WarrantyListItem pour l'affichage dans la liste
    const listItem: WarrantyListItem = {
      uuid: created.uuid,
      code: created.code,
      name: created.name,
      warrantyType: created.warrantyType,
      vehicleType: created.vehicleType,
      duration: created.duration,
      kmLimit: created.kmLimit,
      financialCap: created.financialCap,
      sellingPrice: created.sellingPrice,
      servicePrice: created.servicePrice,
      assistanceFormulaId: created.assistanceFormulaId ?? null,
      assistancePrice: created.assistancePrice ?? null,
      commission: created.commission,
      commissionType: created.commissionType,
      isActive: created.isActive,
      isBroadcast: created.isBroadcast,
      isMensualisable: created.isMensualisable,
      isCashPayment: created.isCashPayment,
      createdAt: created.createdAt || new Date().toISOString(),
      color: created.color,
      order: created.order,
      societyId: created.societyId,
      societyName: society?.name || '',
      societyCode: society?.code || '',
      societyType: '',
      monthlyPlateformFees: config?.monthlyPlateformFees || 0,
      partsCount: partIds?.length || created.parts?.length || 0,
    };

    updateFormData({
      customWarranty: listItem,
      customWarrantyRaw: created,
      customWarrantyPartIds: partIds || [],
      warrantyUuid: created.uuid,
      warrantyName: created.name,
      vehicleType: created.vehicleType,
      commission: created.commission || 0,
      commissionType: (created.commissionType as 1 | 2) || 1,
    });
  };

  // Remonter la validation au container.
  // On exige une date de prise d'effet PLAUSIBLE (bloque l'aberration année 2
  // chiffres « 0026 ») — et idem la date de fin quand c'est une extension.
  const isSubscriptionDateValid =
    !!formData.subscriptionDate && isPlausibleContractDate(formData.subscriptionDate);
  // On NE rend PAS extensionEndDate obligatoire (comportement d'origine : vide
  // autorisé, le back calcule la date de fin). On bloque uniquement une année
  // aberrante si une valeur est saisie (périmètre robustesse des dates).
  const isExtensionEndDateValid =
    !isSelectedWarrantyExtension ||
    !formData.extensionEndDate ||
    isPlausibleContractDate(formData.extensionEndDate);
  const canProceed =
    !!formData.warrantyUuid && isSubscriptionDateValid && isExtensionEndDateValid;
  useEffect(() => {
    onCanProceedChange(canProceed);
  }, [canProceed, onCanProceedChange]);

  return (
    <div className="tw-max-w-7xl tw-mx-auto tw-p-2 sm:tw-p-4">
      {/* BLOC GARANTIE - Pleine largeur */}
      {formData.societyId && (
        <>
          <div id="warranty-select" className={`tw-p-3 sm:tw-p-6 tw-mb-6 tw-rounded-xl ${showErrors && !formData.warrantyUuid ? 'tw-border-2 tw-border-red-500' : ''}`}>
            <div className="tw-flex tw-items-center tw-gap-2 tw-mb-4">
              <Shield className="tw-w-4 tw-h-4 tw-text-affinicar-primary" />
              <h4 className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-mb-0">Sélection de {wording.withArticle} <Tooltip content="Choisissez la formule de garantie à appliquer au contrat. Vous pouvez aussi créer une garantie personnalisée" /></h4>
            </div>

            {/* Filtres type de véhicule */}
            {filteredVehicleTypeOptions.length > 1 && (
              <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-mb-3">
                {filteredVehicleTypeOptions.map((opt) => {
                  const isSelected = selectedVehicleType === opt.value;
                  const iconMap: Record<string, React.ReactNode> = {
                    '': null,
                    '0': <Car className="tw-w-4 tw-h-4" />,
                    '1': <Crown className="tw-w-4 tw-h-4" />,
                    '2': <Bike className="tw-w-4 tw-h-4" />,
                  };
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        setSelectedVehicleType(opt.value);
                        updateFormData({ warrantyUuid: undefined, warrantyName: undefined });
                      }}
                      className={`
                        tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5
                        tw-rounded-full tw-text-sm tw-font-medium tw-border tw-transition-all tw-select-none
                        ${isSelected
                          ? 'tw-bg-affinicar-primary tw-text-white tw-border-affinicar-primary tw-shadow-sm'
                          : 'tw-bg-white tw-text-gray-700 tw-border-gray-300 hover:tw-border-gray-400'
                        }
                      `}
                    >
                      {iconMap[opt.value]}
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Filtre type de contrat */}
            {warrantyTypeOptions.length > 1 && (
              <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-mb-3">
                {warrantyTypeOptions.map((opt) => {
                  const isSelected = selectedWarrantyType === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        setSelectedWarrantyType(opt.value);
                        if (opt.value === WARRANTY_TYPES.EXTENSION.toString()) {
                          setSelectedDuration('');
                        }
                        updateFormData({ warrantyUuid: undefined, warrantyName: undefined });
                      }}
                      className={`
                        tw-px-3 tw-py-1.5 tw-rounded-full tw-text-sm tw-font-medium tw-border tw-transition-all
                        ${isSelected
                          ? 'tw-bg-affinicar-primary tw-text-white tw-border-affinicar-primary tw-shadow-sm'
                          : 'tw-bg-white tw-text-gray-700 tw-border-gray-300 hover:tw-border-gray-400'
                        }
                      `}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Filtre assistance — masqué si l'offre ne concerne pas la société du
                contrat, ou si toutes les garanties chargées ont le même statut */}
            {isAssistanceVisible && assistanceOptions.length > 1 && (
              <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-mb-3">
                {assistanceOptions.map((opt) => {
                  const isSelected = selectedAssistance === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        setSelectedAssistance(opt.value);
                        // Comme les filtres véhicule et type : une garantie déjà
                        // choisie peut devenir invisible, et le bloc de
                        // tarification plus bas resterait ouvert dessus.
                        updateFormData({ warrantyUuid: undefined, warrantyName: undefined });
                      }}
                      className={`
                        tw-px-3 tw-py-1.5 tw-rounded-full tw-text-sm tw-font-medium tw-border tw-transition-all
                        ${isSelected
                          ? 'tw-bg-affinicar-primary tw-text-white tw-border-affinicar-primary tw-shadow-sm'
                          : 'tw-bg-white tw-text-gray-700 tw-border-gray-300 hover:tw-border-gray-400'
                        }
                      `}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Filtres durée (masqué pour les extensions) */}
            {!isExtensionSelected && durationOptions.length > 1 && (
              <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-mb-4">
                {durationOptions.map((opt) => {
                  const isSelected = selectedDuration === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setSelectedDuration(opt.value)}
                      className={`
                        tw-px-3 tw-py-1.5 tw-rounded-full tw-text-sm tw-font-medium tw-border tw-transition-all
                        ${isSelected
                          ? 'tw-bg-affinicar-primary tw-text-white tw-border-affinicar-primary tw-shadow-sm'
                          : 'tw-bg-white tw-text-gray-700 tw-border-gray-300 hover:tw-border-gray-400'
                        }
                      `}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Compteur de résultats */}
            {!isLoading && (
              <p className="tw-text-xs tw-text-gray-500 tw-mb-4">
                {filteredWarranties.length} {wording.singularLower}{filteredWarranties.length > 1 ? 's' : ''} disponible{filteredWarranties.length > 1 ? 's' : ''}
              </p>
            )}

            {/* Liste des garanties */}
            {isLoading ? (
              <div className="tw-text-center tw-py-12">
                <div className="spinner-border tw-text-affinicar-primary" role="status">
                  <span className="visually-hidden">Chargement...</span>
                </div>
              </div>
            ) : (
              <div className="tw-pb-4 tw-px-2 tw-pt-2">
                {filteredWarranties.length === 0 && (
                  <p className="tw-text-gray-500 tw-text-sm tw-mb-4">
                    Aucune {wording.singularLower} disponible pour ces critères
                  </p>
                )}
                <div className={`tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-4 ${isPdfPanelOpen ? 'xl:tw-grid-cols-4' : 'xl:tw-grid-cols-5'} tw-gap-4`}>
                  {filteredWarranties.map((w) => (
                    <WarrantySelectionCard
                      key={w.uuid}
                      warranty={w}
                      isSelected={formData.warrantyUuid === w.uuid}
                      onSelect={() => {
                        updateFormData({
                          warrantyUuid: w.uuid,
                          warrantyName: w.name,
                          vehicleType: w.vehicleType,
                          commission: w.commission || 0,
                          commissionType: (w.commissionType as 1 | 2) || 1,
                        });
                        setTimeout(() => document.getElementById('payment-mode-block')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
                      }}
                      onEdit={isWarrantyPolicyOwner && customWarranty?.uuid === w.uuid ? () => {
                        setIsEditMode(true);
                        setIsCreateModalOpen(true);
                      } : undefined}
                      hidePrices={config?.hidePricesToChildren}
                      hideMonthlyPrice={!societyHasStripe}
                      showAssistance={isAssistanceVisible}
                    />
                  ))}

                  {isWarrantyPolicyOwner && (
                    <div
                      onClick={() => setIsCreateModalOpen(true)}
                      className="tw-bg-white tw-rounded-2xl tw-overflow-hidden tw-transition-all tw-border-2 tw-border-dashed tw-border-gray-300 tw-cursor-pointer tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-min-h-[200px] tw-h-full hover:tw-border-affinicar-primary hover:tw-bg-affinicar-primary/5 tw-group"
                    >
                      <div className="tw-w-12 tw-h-12 tw-rounded-full tw-bg-gray-100 tw-flex tw-items-center tw-justify-center group-hover:tw-bg-affinicar-primary/10 tw-transition-colors">
                        <Plus className="tw-w-6 tw-h-6 tw-text-gray-400 group-hover:tw-text-affinicar-primary tw-transition-colors" />
                      </div>
                      <div className="tw-text-center tw-px-4">
                        <p className="tw-text-sm tw-font-semibold tw-text-gray-600 group-hover:tw-text-affinicar-primary tw-transition-colors tw-mb-1">
                          {wording.singular} personnalisée
                        </p>
                        <p className="tw-text-xs tw-text-gray-400 tw-mb-0">
                          Créer sur mesure
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {showErrors && !formData.warrantyUuid && (
              <div className="tw-text-red-500 tw-text-sm tw-mt-2 tw-px-3">
                Veuillez sélectionner {wording.withArticle} pour continuer
              </div>
            )}
          </div>
        </>
      )}

      {/* CHECKBOX INTERMÉDIAIRE DE VENTE */}
      {config?.isIntermediary ? (
        <div className="tw-mb-6 tw-p-4 tw-bg-amber-50 tw-border tw-border-amber-200 tw-rounded-xl">
          <Checkbox
            checked={formData.isIntermediary}
            onChange={(checked) => updateFormData({ isIntermediary: checked })}
            label="Vendre ce contrat en tant qu'intermédiaire de vente"
          />
          <p className="tw-text-xs tw-text-gray-600 tw-mt-1.5 tw-mb-0 tw-ml-8">
            Si coché, le contrat utilisera les conditions générales de prestation et la société sera désignée comme « Intermédiaire de vente » dans le PDF.
          </p>
        </div>
      ) : null}

      {/* Configuration : Paiement + Date + Signature */}
      <div>
        <div className="tw-space-y-4">

            {/* TARIFICATION */}
            {formData.warrantyUuid && <>
            <div id="payment-mode-block" className="tw-bg-affinicar-primary/5 tw-rounded-xl tw-p-6 tw-border-l-4 tw-border-l-grey">
              <p className="tw-text-sm tw-text-gray-500 tw-mb-4">
                Définissez le tarif de la garantie facturé au client
              </p>

              <div>
                  <FormInput
                      type="number"
                      label={priceInfo.label}
                      value={
                        priceFieldConfig.field === 'monthlyPriceWithFees'
                          ? (formData.monthlyPriceWithFees?.toString() || '0')
                          : priceFieldConfig.useMonthlyInput
                            ? (formData.monthlyPrice?.toString() || '0')
                            : (formData.sellingPrice?.toString() || '0')
                      }
                      onChange={(val) => {
                        if (priceFieldConfig.displayReadOnly || formData.includedOption) return;

                        const numValue = val ? parseFloat(val) : 0;
                        if (priceFieldConfig.field === 'monthlyPriceWithFees') {
                          updateFormData({ monthlyPriceWithFees: numValue });
                        } else if (priceFieldConfig.useMonthlyInput) {
                          updateFormData({ monthlyPrice: numValue });
                        } else {
                          if (numValue === 0 && canBeIncluded && !formData.includedOption) {
                            updateFormData({ sellingPrice: 0, includedOption: true, paymentType: 1, includedOptionLabel: formData.includedOptionLabel || 'Incluse' });
                          } else if (numValue > 0 && formData.includedOption) {
                            updateFormData({ sellingPrice: numValue, includedOption: false, includedOptionLabel: undefined });
                          } else {
                            updateFormData({ sellingPrice: numValue });
                          }
                        }
                      }}
                      placeholder="Ex: 500"
                      required
                      disabled={priceFieldConfig.displayReadOnly || formData.includedOption || !canEditPrice}
                      suffix="€"
                      tooltip="Montant TTC facturé au client. Modifiez ce montant pour ajuster votre marge"
                    />

                  {/* Option Inclusion */}
                  {canBeIncluded && (
                    <div className="tw-mt-3">
                      <Checkbox
                        checked={formData.includedOption}
                        onChange={(checked) => {
                          if (checked) {
                            updateFormData({ includedOption: true, sellingPrice: 0, paymentType: 1, includedOptionLabel: 'Incluse' });
                          } else {
                            const restoredData: Record<string, any> = { includedOption: false, includedOptionLabel: undefined };
                            if (warranty && config) {
                              const isMonthlyOnly = !warranty.isCashPayment && warranty.isMensualisable;
                              const isBoth = warranty.isCashPayment && warranty.isMensualisable;
                              if (isMonthlyOnly) {
                                restoredData.monthlyPrice = warranty.sellingPrice;
                                restoredData.sellingPrice = warranty.sellingPrice * warranty.duration;
                              } else if (isBoth) {
                                restoredData.sellingPrice = warranty.sellingPrice;
                                restoredData.monthlyPriceWithFees = Number(calculateMonthlyPrice(
                                  warranty.sellingPrice / warranty.duration,
                                  config.monthlyPlateformFees
                                ).toFixed(2));
                              } else {
                                restoredData.sellingPrice = warranty.sellingPrice;
                              }
                            }
                            updateFormData(restoredData);
                          }
                        }}
                        label={
                          <span className="tw-flex tw-items-center tw-gap-3">
                            <span className="tw-flex tw-items-center tw-gap-1">{wording.singular} incluse <Tooltip content="Cochez si la garantie est offerte au client (incluse dans le prix de vente du véhicule)" /></span>
                            {formData.includedOption && (
                              <>
                                <span className="tw-text-gray-400">|</span>
                                <span className="tw-text-gray-700 tw-font-normal">Libellé PDF :</span>
                                <select
                                  value={formData.includedOptionLabel || 'Incluse'}
                                  onChange={(e) => { e.stopPropagation(); updateFormData({ includedOptionLabel: e.target.value }); }}
                                  onClick={(e) => e.stopPropagation()}
                                  className="tw-px-2 tw-py-0.5 tw-border tw-border-gray-300 tw-rounded-lg tw-text-sm tw-bg-white focus:tw-ring-2 focus:tw-ring-affinicar-primary focus:tw-border-affinicar-primary"
                                >
                                  <option value="Incluse">Incluse</option>
                                  <option value="Offerte">Offerte</option>
                                  <option value="0 €">0 €</option>
                                </select>
                              </>
                            )}
                          </span>
                        }
                      />
                    </div>
                  )}
              </div>
            </div></>}

            {/* DATE PRISE D'EFFET */}
            <div id="subscription-date-input" className="tw-bg-affinicar-primary/5 tw-rounded-xl tw-p-6 tw-border-l-4 tw-border-l-grey">
              <p className="tw-text-sm tw-text-gray-500 tw-mb-4">
                Date à laquelle la couverture de garantie entre en vigueur
              </p>
              <FormInput
                type="date"
                label="Date de prise d'effet du contrat"
                value={formData.subscriptionDate}
                onChange={(val) => updateFormData({ subscriptionDate: val })}
                required
                min="2020-01-01"
                max={`${new Date().getFullYear() + 5}-12-31`}
                error={
                  showErrors
                    ? !formData.subscriptionDate
                      ? 'Ce champ est obligatoire'
                      : !isSubscriptionDateValid
                        ? `Année invalide (attendue entre 2020 et ${new Date().getFullYear() + 5})`
                        : undefined
                    : undefined
                }
                tooltip="Date à laquelle la couverture de garantie commence"
              />

              {dateInfo && (
                <div className="tw-flex tw-items-center tw-gap-2 tw-mt-2">
                  <Info className="tw-w-4 tw-h-4 tw-text-blue-600" />
                  <span className="tw-text-sm tw-text-blue-600">{dateInfo.message}</span>
                </div>
              )}

              {dateValidation && (
                <div className="tw-flex tw-items-center tw-gap-2 tw-mt-2">
                  <AlertTriangle className="tw-w-4 tw-h-4 tw-text-orange-600" />
                  <span className="tw-text-sm tw-text-orange-600">{dateValidation.message}</span>
                </div>
              )}

              {/* Date de fin pour les extensions de garantie constructeur */}
              {isSelectedWarrantyExtension && (
                <div className="tw-mt-4">
                  <FormInput
                    type="date"
                    label="Date de fin du contrat"
                    value={formData.extensionEndDate || ''}
                    onChange={(val) => updateFormData({ extensionEndDate: val || undefined })}
                    required
                    min="2020-01-01"
                    max={`${new Date().getFullYear() + 5}-12-31`}
                    error={
                      showErrors && formData.extensionEndDate && !isPlausibleContractDate(formData.extensionEndDate)
                        ? `Année invalide (attendue entre 2020 et ${new Date().getFullYear() + 5})`
                        : undefined
                    }
                    tooltip="Date de fin de l'extension de garantie constructeur. La durée sera calculée automatiquement."
                  />

                  {extensionDurationInfo?.type === 'error' && (
                    <div className="tw-flex tw-items-center tw-gap-2 tw-mt-2">
                      <AlertTriangle className="tw-w-4 tw-h-4 tw-text-red-600" />
                      <span className="tw-text-sm tw-text-red-600">{extensionDurationInfo.message}</span>
                    </div>
                  )}

                  {extensionDurationInfo?.type === 'info' && (
                    <div className="tw-flex tw-items-center tw-gap-2 tw-mt-2">
                      <Info className="tw-w-4 tw-h-4 tw-text-blue-600" />
                      <span className="tw-text-sm tw-text-blue-600">{extensionDurationInfo.message}</span>
                    </div>
                  )}
                </div>
              )}
            </div>


        </div>
      </div>

      {formData.societyId && isWarrantyPolicyOwner && (
        <CreateCustomWarrantyModal
          isOpen={isCreateModalOpen}
          onClose={() => { setIsCreateModalOpen(false); setIsEditMode(false); }}
          onCreated={handleWarrantyCreated}
          societyId={formData.societyId}
          vehicleType={formData.vehicleType}
          wording={wording}
          editWarranty={isEditMode ? customWarrantyRaw : null}
          editPartIds={isEditMode ? customWarrantyPartIds : undefined}
          allowedVehicleTypes={allowedVehicleTypes}
          autoWarrantyType={autoWarrantyType}
        />
      )}
    </div>
  );
}
